import { Injectable } from '@angular/core';
import { DcBaseRequester } from '@core/dc-base.requester';
import { LanguagePreference } from '@core/domain/preferences';
import {
	CachePolicy,
	CachePolicyEntity,
	DcBaseInitializationService,
	UrlService,
} from '@datachain/ui-sdk/common';
import { ProductInstanceModel } from '@dc-console-api';
import { IFeatureModel } from '@requesters/models/feature.model';
import { Observable } from 'rxjs';

import {
	IPreferencesModel,
	SupportedLanguages,
} from './models/preferences.model';
import { IProfileModel } from './models/profile.model';
import { ITokenGenerate } from './models/token-generate.model';
import { ITokenModel } from './models/token.model';
import { PreferenceConfig } from '../features/account/components/preference-config-popup/preference-config';
import { TokenConfig } from '../features/account/components/token-creator-modal/token-config';

@Injectable({
	providedIn: 'root',
})
export class AccountRequester extends DcBaseRequester<unknown> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	private readonly languageMapping: { [k: string]: SupportedLanguages } = {
		[LanguagePreference.Browser]: SupportedLanguages.BROWSER,
		[LanguagePreference.Fr]: SupportedLanguages.FR,
		[LanguagePreference.En]: SupportedLanguages.EN,
	};

	public constructor(
		private readonly urlService: UrlService,
	) {
		super();
		this.resource = '';
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getCurrentUser(): Observable<IProfileModel> {
		return this.get<IProfileModel>(
			'/account',
			CachePolicyEntity.build({
				policy: CachePolicy.NetworkAndCache,
				evictTimeoutInSeconds: 600,
				isCacheable: true,
			}),
		);
	}

	public getPreferences(): Observable<IPreferencesModel> {
		const endpoint = DcBaseInitializationService.getResource(
			'MYSELF_PREFERENCES_PRIVATE_API',
		);

		const url = this.urlService.generateUrl(endpoint, {}, {});

		return this.http.get<IPreferencesModel>(url);
	}

	public updatePreferences(
		preferences: PreferenceConfig,
	): Observable<IPreferencesModel> {
		const endpoint = DcBaseInitializationService.getResource(
			'MYSELF_PREFERENCES_PRIVATE_API',
		);

		const url = this.urlService.generateUrl(endpoint, {}, {});

		return this.http.put<IPreferencesModel>(url, {
			language: this.languageMapping[preferences.language],
		});
	}

	public getAvailableInstanceModules(): Observable<
		ReadonlyArray<ProductInstanceModel>
	> {
		const endpoint = DcBaseInitializationService.getResource(
			'MYSELF_INSTANCES_PRIVATE_API',
		);
		return this.get<ReadonlyArray<ProductInstanceModel>>(
			endpoint,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				evictTimeoutInSeconds: 3600,
				isCacheable: true,
			}),
		);
	}

	public getAvailableAppFeatures(): Observable<IFeatureModel> {
		const endpoint = DcBaseInitializationService.getResource(
			'FEATURES_PRIVATE_API',
		);
		return this.get<IFeatureModel>(
			endpoint,
			CachePolicyEntity.build({
				policy: CachePolicy.NetworkAndCache,
				evictTimeoutInSeconds: 3600,
				isCacheable: true,
			}),
		);
	}

	public generateToken(
		organizationId: number,
		userId: number,
		token: TokenConfig,
	): Observable<ITokenModel> {
		const body = this.prepareTokenPayloadForSave(userId, token);

		const endpoint = DcBaseInitializationService.getResource(
			'TOKENS_BY_USER_ID_PRIVATE_API',
		);
		const url = this.urlService.generateUrl(
			endpoint,
			{
				orgId: organizationId,
				userId,
			},
			{},
		);
		return this.http.post<ITokenModel>(url, body);
	}

	private prepareTokenPayloadForSave(
		userId: number,
		token: TokenConfig,
	): ITokenGenerate {
		return {
			label: token.label,
			validityStartDate: token.activationDate?.valueOf().toString(),
			expirationDate: token.expirationDate?.valueOf().toString(),
			sourceInstanceCode: token.fromModuleCode,
			targetInstanceCode: token.forModuleCode,
			user: {
				id: userId,
			},
		};
	}
}
